import {gql} from '@apollo/client'
import * as t from 'io-ts'
import React, {useEffect, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import AutosizeInput from 'react-input-autosize'
import {useNavigate} from 'react-router'
import {Link} from 'react-router-dom'
import styled from 'styled-components'
import {ERROR_CODE_NON_UNIQUE} from '../../../common/types/constants'
import {Error} from '../../../components/auth/layout'
import {
  EditableTitle,
  PageActions,
  PageBody,
  PageHeader
} from '../../../components/page-styles'
import {Button} from '../../../components/library/buttons'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {Tag} from '../../../components/library/tag/tag'
import {Loader} from '../../../components/loader'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {AlertDialog} from '../../../hooks/use-confirm'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useToast} from '../../../lib/toast'
import {OrganisationNode} from '../../../organisation'
import {
  IconHeading,
  Section,
  SectionContent,
  SectionInfo
} from '../../configuration/components'
import {
  RoleQuery,
  useRoleLazyQuery,
  useSaveRolePermissionsMutation
} from './generated'
import {ListResources} from './list-resources'

export const PageHeaderContent = styled.div`
  display: flex;
  align-items: center;
  * {
    margin: 0 10px 0 0;
  }
`

const PermissionsCodec = t.record(
  t.string,
  t.type({
    active: t.boolean,
    action: t.union([t.literal('ADMIN'), t.literal('EDIT'), t.literal('VIEW')])
  })
)

export type Permissions = t.TypeOf<typeof PermissionsCodec>

const RoleCodec = t.intersection([
  t.type({
    name: t.string,
    permissions: PermissionsCodec
  }),
  t.partial({
    id: t.string,
    description: t.union([t.string, t.null])
  })
])

type Role = t.TypeOf<typeof RoleCodec>

export const Role = ({
  id,
  organisation
}: {
  id?: string
  organisation: OrganisationNode
}) => {
  const [state, setState, changed, resetState] = useStateChanged<Role>({
    name: '',
    description: '',
    permissions: {}
  })
  const canEdit = organisation.hasPermission(
    PermissionResource.CustomRole,
    PermissionAction.Edit
  )
  const [error, setError] = useState<string | undefined>()
  const toast = useToast()
  const navigate = useNavigate()
  const setInitialState = (data?: RoleQuery) => {
    if (data?.customRole) {
      resetState({
        id: data.customRole.id,
        name: data.customRole.name,
        description: data.customRole.description,
        permissions: data.customRole.permissions.nodes.reduce(
          (acc: Permissions, p) => ({
            ...acc,
            [p.resource]: {
              active: true,
              action: p.action
            }
          }),
          {}
        )
      })
    } else {
      resetState({name: '', description: '', permissions: {}})
    }
  }
  const [query, {data, loading}] = useRoleLazyQuery({
    onCompleted: setInitialState // set internal state on load
  })
  const [save, {loading: saving}] = useSaveRolePermissionsMutation({
    onCompleted: saved => {
      toast('Changes saved')
      setError(undefined)
      const newId = saved?.setRolePermissions?.customRole?.id
      if (newId) {
        resetState(s => ({
          ...s,
          id: newId
        }))
      }
    },
    onError: e => {
      if (e.message.includes(ERROR_CODE_NON_UNIQUE)) {
        setError('Sorry a role with this name already exists.')
      } else {
        setError(e.message)
      }
    }
  })
  const validate = (s: Role): {valid: false; error: string} | {valid: true} => {
    // Note if more rules are added here you'll need to update the Alert confirmation text, see render
    if (!s.name) {
      return {valid: false, error: 'Please name this role'}
    }
    return {valid: true}
  }

  useEffect(() => {
    if (id) {
      query({
        variables: {id, organisationId: organisation.id}
      })
    }
  }, [id, organisation.id, query])

  useEffect(() => {
    // Update the page URL after creating a new role
    if (state.id && !id) {
      navigate(`../role/${state.id}`, {replace: true})
    }
  }, [state.id, id, navigate])

  const onSave = async () => {
    const res = validate(state)
    if (res.valid) {
      setError(undefined)
      const payload = {
        ...state,
        organisationId: organisation.id,
        permissions: Object.entries(state.permissions).map(
          ([resource, value]) => {
            return {
              resource,
              ...value
            }
          }
        )
      }
      await save({
        // @ts-ignore complains that "resource" and "action" aren't in the PermissionResource and PermissionAction enums, they are
        variables: {rolePermissions: payload}
      })
    } else {
      setError(res.error)
    }
  }

  const isValid = validate(state).valid

  return loading ? (
    <Loader />
  ) : (
    <>
      <Helmet>
        <title>{state.name}</title>
      </Helmet>
      <PageHeader>
        <PageHeaderContent>
          <Link to={state.id ? '../../roles' : '../roles'}>
            <Icon name="back_large" />
          </Link>
          <EditableTitle>
            <AutosizeInput
              inputRef={instance => instance?.focus()}
              type="text"
              readOnly={!canEdit}
              placeholder="Untitled role"
              value={state.name}
              onChange={e => {
                setState(g => ({...g, name: e.target.value}))
              }}
            />
          </EditableTitle>
          <Tag appearance="blue">Role</Tag>
        </PageHeaderContent>
        <PageActions>
          {changed && canEdit && (
            <>
              <Button
                size="small"
                appearance="secondary"
                onClick={() => {
                  setInitialState(data)
                  setError(undefined)
                }}
              >
                Discard changes
              </Button>
              <Button
                size="small"
                appearance="primary"
                disabled={saving}
                onClick={onSave}
              >
                {state.id ? 'Update role' : 'Create role'}
              </Button>
            </>
          )}
        </PageActions>
      </PageHeader>
      <PageBody>
        {error ? <Error>{error}</Error> : null}
        <TextInput
          style={{marginBottom: '30px'}}
          upLabel="Role description"
          label="Role description"
          disabled={!canEdit}
          readOnly={!canEdit}
          value={state.description ?? ''}
          onChange={description => setState(g => ({...g, description}))}
        />
        <Section>
          <header>
            <IconHeading>
              <Icon name="key" /> Resources and permissions
            </IconHeading>
          </header>
          <SectionInfo>
            <p>Enable access to specific resources.</p>
          </SectionInfo>

          <SectionContent>
            <ListResources
              organisation={organisation}
              permissions={state.permissions}
              onChange={p => setState(s => ({...s, permissions: p}))}
            />
          </SectionContent>
        </Section>
      </PageBody>
      <AlertDialog
        isBlocking={changed}
        onSave={isValid ? onSave : undefined}
        saveText={state.id ? 'Update role' : 'Create role'}
        cancelText="Keep editing"
        discardText="Discard"
      >
        {isValid
          ? 'Would you like to save this role before you leave?'
          : 'If you would like to create this role you will need to give it a name.'}
      </AlertDialog>
    </>
  )
}

Role.fragments = {
  role: gql`
    fragment RolePermissions on CustomRole {
      id
      name
      description
      permissions: rolePermissionsByOrganisationIdAndRoleId {
        nodes {
          resource
          action
        }
      }
    }
  `
}

gql`
  query Role($id: String!, $organisationId: String!) {
    customRole(id: $id, organisationId: $organisationId) {
      ...RolePermissions
    }
  }
  ${Role.fragments.role}
`

gql`
  mutation SaveRolePermissions($rolePermissions: RolePermissionsInput) {
    setRolePermissions(input: $rolePermissions) {
      customRole {
        ...RolePermissions
      }
    }
  }
  ${Role.fragments.role}
`
